/**
 * Fixed popover anchored to a DOM element; portal to document.body to avoid overflow clipping.
 * Supports hover (desktop) and tap-outside / Escape to close (mobile and desktop).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CHART_POPOVER_DEFAULT_WIDTH,
  computeChartPopoverPosition,
} from '../utils/chart-popover-position';
import { trackEvent } from '../../../../../utils/analytics/analytics';

export interface ChartPopoverPortalProps {
  open: boolean;
  anchorEl: Element | null;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  /** Estimated width for horizontal centering (px) */
  width?: number;
  /** Full-screen dismiss layer (use false when opening from hover so the cursor can reach the popover) */
  showBackdrop?: boolean;
  /** Keep popover open when pointer moves from anchor to panel */
  onPopoverMouseEnter?: () => void;
  onPopoverMouseLeave?: () => void;
}

export const ChartPopoverPortal: React.FC<ChartPopoverPortalProps> = ({
  open,
  anchorEl,
  onClose,
  title,
  children,
  width = CHART_POPOVER_DEFAULT_WIDTH,
  showBackdrop = true,
  onPopoverMouseEnter,
  onPopoverMouseLeave,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Every salary/company chart popover routes through this portal, so one
  // effect here covers chart engagement across all charts.
  useEffect(() => {
    if (!open) return;
    trackEvent({
      name: 'salary_insight_interaction',
      params: {
        interaction_type: 'chart_popover_open',
        locale: typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') || undefined : undefined,
      },
    });
  }, [open]);

  const reposition = useCallback(() => {
    if (!open || !anchorEl || typeof window === 'undefined') return;
    const rect = anchorEl.getBoundingClientRect();
    const el = popoverRef.current;
    const h = el?.offsetHeight ?? 160;
    const { top, left } = computeChartPopoverPosition(rect, width, h, window.innerWidth, window.innerHeight);
    setStyle({ top, left });
  }, [open, anchorEl, width]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition, open, children]);

  useEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (typeof document === 'undefined' || !open || !anchorEl) {
    return null;
  }

  return createPortal(
    <>
      {showBackdrop ? (
        <button
          type="button"
          aria-label="Close chart details"
          className="fixed inset-0 z-[1390] cursor-default bg-black/15 dark:bg-black/35"
          onClick={onClose}
        />
      ) : null}
      <div
        ref={popoverRef}
        role="dialog"
        aria-modal={showBackdrop}
        className="fixed z-[1400] max-h-[min(70vh,420px)] overflow-y-auto rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] p-3 shadow-xl ring-1 ring-black/5 dark:border-[#4A3520] dark:bg-[#1F1509] dark:ring-white/10"
        style={{
          top: style.top,
          left: style.left,
          width: `min(${width}px, calc(100vw - 16px))`,
        }}
        onMouseEnter={onPopoverMouseEnter}
        onMouseLeave={onPopoverMouseLeave}
      >
        <div className="border-b border-[#DDD5C8]/80 pb-2 mb-2 dark:border-[#4A3520]">
          <p className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">{title}</p>
        </div>
        <div className="text-xs text-[#3D3529] dark:text-[#C4A882] space-y-1.5">{children}</div>
      </div>
    </>,
    document.body,
  );
};

ChartPopoverPortal.displayName = 'ChartPopoverPortal';
